import {
  buildProjectCreateFieldEditPrompt,
  parseProjectCreateFieldEditDualAnswer,
  resolveFieldEditDualAnswer,
  tryInferFieldEditFromProse,
} from '@/utils/projectCreateFieldEditParse';

export function buildAiPageContentFieldEditPrompt(
  quoted: string,
  fieldLabel: string,
  userRequirement: string,
  options?: { fieldPath?: string; positionDescription?: string; scopeLabel?: string },
): string {
  const scope = String(options?.scopeLabel ?? '当前页面').trim() || '当前页面';
  const pos = String(options?.positionDescription ?? '').trim();
  const where = pos || `${scope} · 「${fieldLabel}」区域内的选中片段`;
  const base = buildProjectCreateFieldEditPrompt(quoted, fieldLabel, userRequirement, {
    fieldPath: options?.fieldPath,
    positionDescription: where,
  }).replace('【系统任务：项目创建页-信息片段改写】', `【系统任务：${scope}-内容片段改写】`)
    .replace(
      '用户正在「创建项目」页的「项目信息」表单中，选中了某一具体位置的文本片段，并说明要如何修改。',
      `用户正在「${scope}」中选中了一段文本，并说明要如何修改。`,
    );
  if (options?.fieldPath === 'document-body') {
    return [
      base,
      '',
      '【文档正文改写特别要求】',
      'JSON 中 revised 字段是写入 Word 文档正文的纯文本/HTML 片段，不是 Markdown。',
      '禁止使用 #、##、**、- 列表等 Markdown 语法；如需标题请直接写标题文字，如需强调仅用普通中文表述。',
      '若必须分级，可使用「一、」「（1）」等中文序号，不要用 # 标题。',
      '跨段落/跨章节改写时，revised 必须用换行符 \\n 分隔各段/各标题行，以保留原文的段落与章节结构。',
      'revised 末尾不要添加多余空行；若原文最后一行仅部分选中，改写结果最后一行末尾也不要加换行符，以便与后续正文衔接。',
    ].join('\n');
  }
  return base;
}

export function parseAiPageContentFieldEditDualAnswer(answer: string) {
  return parseProjectCreateFieldEditDualAnswer(answer);
}

export function resolveAiPageContentFieldEditDualAnswer(
  answer: string,
  thoughtRaw: unknown,
  quoted?: string,
) {
  return resolveFieldEditDualAnswer(answer, thoughtRaw, quoted);
}

export function tryInferAiPageContentFieldEditFromProse(answer: string, quoted: string) {
  return tryInferFieldEditFromProse(answer, quoted);
}

const PAGE_CONTENT_EDIT_INTENT_RE =
  /(?:改成|改为|修改|替换|润色|优化|完善|重写|缩写|扩写|删除|去掉|补充|纠正|修正|更新为|调整为|改为|换成|写成)/;

const PAGE_CONTENT_DISCUSS_INTENT_RES: RegExp[] = [
  /是什么意思/,
  /什么意思/,
  /什么含义/,
  /含义(?:是|为)?什么/,
  /解释(?:一下|下)?/,
  /为什么/,
  /为何/,
  /怎么理解/,
  /如何理解/,
  /指的是什么/,
  /说的是什么/,
  /能否说明/,
  /帮我理解/,
  /请问/,
  /\?$/,
  /？$/,
];

/** 判断用户是在询问引用片段含义，而非要求改写 */
export function isPageContentDiscussIntent(userRequirement: string): boolean {
  const text = String(userRequirement ?? '').trim();
  if (!text) return false;
  if (PAGE_CONTENT_EDIT_INTENT_RE.test(text)) return false;
  return PAGE_CONTENT_DISCUSS_INTENT_RES.some((re) => re.test(text));
}
